import { CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class CartSizeQuantityGrid extends HTMLElement {
  #abortController = new AbortController();
  #debounceTimeout;

  connectedCallback() {
    // Create a fresh AbortController on every connect. The element can be
    // detached and re-attached by section morphing; the previous controller is
    // aborted in disconnectedCallback, so reusing it here would attach the
    // listeners with an already-aborted signal (i.e. not at all), leaving the
    // grid unresponsive until a full page refresh.
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });

    for (const input of this.querySelectorAll('[data-variant-id]')) {
      if (input instanceof HTMLInputElement) {
        this.#syncControlState(input);
      }
    }
  }

  disconnectedCallback() {
    this.#abortController.abort();
    window.clearTimeout(this.#debounceTimeout);
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-action]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    if (button.dataset.action === 'remove-product') {
      this.closest('.cart-items__table-row')?.classList.add('removing');
      if (this.querySelector('[data-size]')) {
        this.#updateCart({ remove: true });
      } else {
        this.#removeVariantLines();
      }
      return;
    }

    const input = button.parentElement?.querySelector('[data-variant-id]');
    if (!(input instanceof HTMLInputElement)) return;

    const currentQuantity = Number(input.value) || 0;
    const nextQuantity = button.dataset.action === 'increase'
      ? currentQuantity + 1
      : Math.max(0, currentQuantity - 1);

    input.value = nextQuantity.toString();
    this.#clampInput(input);
    this.#syncControlState(input);
    this.#scheduleUpdate();
  };

  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-variant-id]')) return;

    event.target.value = Math.max(0, Number(event.target.value) || 0).toString();
    this.#clampInput(event.target);
    this.#syncControlState(event.target);
    this.#scheduleUpdate();
  };

  #handleKeydown = (event) => {
    if (event.key !== 'Enter') return;

    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-variant-id]')) return;

    // The quantity inputs live inside the cart <form>, whose submit button is
    // Checkout. Pressing Enter should confirm the quantity being edited, not
    // submit the form and proceed to checkout.
    event.preventDefault();

    input.value = Math.max(0, Number(input.value) || 0).toString();
    this.#clampInput(input);
    this.#syncControlState(input);
    this.#scheduleUpdate();
    input.blur();
  };

  #syncControlState(input) {
    const control = input.closest('.cart-size-quantity-grid__control');
    const decreaseButton = control?.querySelector('[data-action="decrease"]');
    const increaseButton = control?.querySelector('[data-action="increase"]');
    const isEmpty = (Number(input.value) || 0) === 0;
    const inputMax = Number(input.max) || Infinity;
    const reachedInputMax = (Number(input.value) || 0) >= inputMax;
    const reachedTotalMax = this.#hasSharedStockLimit() && this.#getTotalQuantity() >= this.#getMaxTotal();

    control?.classList.toggle('cart-size-quantity-grid__control--empty', isEmpty);
    if (decreaseButton instanceof HTMLButtonElement) {
      decreaseButton.disabled = isEmpty;
    }
    if (increaseButton instanceof HTMLButtonElement) {
      increaseButton.disabled = reachedInputMax || reachedTotalMax;
    }
  }

  #clampInput(input) {
    const inputMax = Number(input.max) || Infinity;
    input.value = Math.min(inputMax, Math.max(0, Number(input.value) || 0)).toString();

    if (!this.#hasSharedStockLimit()) return;

    const maxTotal = this.#getMaxTotal();
    const otherTotal = this.#getTotalQuantity(input);
    const availableForInput = Math.max(0, maxTotal - otherTotal);
    input.value = Math.min(availableForInput, Number(input.value) || 0).toString();

    for (const sizeInput of this.querySelectorAll('[data-variant-id]')) {
      if (sizeInput instanceof HTMLInputElement) {
        this.#syncControlState(sizeInput);
      }
    }
  }

  #getTotalQuantity(excludedInput) {
    let total = 0;
    for (const input of this.querySelectorAll('[data-variant-id]')) {
      if (!(input instanceof HTMLInputElement) || input === excludedInput) continue;
      total += Math.max(0, Number(input.value) || 0);
    }
    return total;
  }

  #hasSharedStockLimit() {
    return this.dataset.maxTotal !== undefined;
  }

  #getMaxTotal() {
    return Math.max(0, Number(this.dataset.maxTotal) || 0);
  }

  #scheduleUpdate() {
    window.clearTimeout(this.#debounceTimeout);
    this.#debounceTimeout = window.setTimeout(() => this.#updateCart(), 350);
  }

  async #updateCart(options = {}) {
    if (this.querySelector('[data-size]')) {
      await this.#updateCartBySizeProperties(options);
      return;
    }

    const updates = {};
    for (const input of this.querySelectorAll('[data-variant-id]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      updates[input.dataset.variantId] = Math.max(0, Number(input.value) || 0);
    }

    const sectionIds = new Set();
    for (const cartItemsComponent of document.querySelectorAll('cart-items-component')) {
      if (cartItemsComponent instanceof HTMLElement && cartItemsComponent.dataset.sectionId) {
        sectionIds.add(cartItemsComponent.dataset.sectionId);
      }
    }

    this.classList.add('cart-size-quantity-grid--updating');

    try {
      const response = await fetch(
        Theme.routes.cart_update_url,
        fetchConfig('json', {
          body: JSON.stringify({
            updates,
            sections: Array.from(sectionIds).join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      const cart = await response.json();

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-size-quantity-grid', {
          source: 'cart-size-quantity-grid',
          itemCount: cart.item_count,
          sections: cart.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.classList.remove('cart-size-quantity-grid--updating');
    }
  }

  async #removeVariantLines() {
    const sectionIds = this.#getCartSectionIds();
    const updates = {};

    for (const input of this.querySelectorAll('[data-variant-id]')) {
      if (input instanceof HTMLInputElement && input.dataset.variantId) {
        updates[input.dataset.variantId] = 0;
      }
    }

    for (const line of this.querySelectorAll('[data-line-key]')) {
      if (line instanceof HTMLElement && line.dataset.lineKey) {
        updates[line.dataset.lineKey] = 0;
      }
    }

    this.classList.add('cart-size-quantity-grid--updating');

    try {
      const response = await fetch(
        Theme.routes.cart_update_url,
        fetchConfig('json', {
          body: JSON.stringify({
            updates,
            sections: Array.from(sectionIds).join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);

      const cart = await response.json();
      if (cart.status) throw new Error(cart.description || cart.message || 'Cart update failed');

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-size-quantity-grid', {
          source: 'cart-size-quantity-grid',
          itemCount: cart.item_count,
          sections: cart.sections,
        })
      );
    } catch (error) {
      console.error(error);
      this.closest('.cart-items__table-row')?.classList.remove('removing');
    } finally {
      this.classList.remove('cart-size-quantity-grid--updating');
    }
  }

  async #updateCartBySizeProperties(options = {}) {
    const sectionIds = this.#getCartSectionIds();
    const existingLineKeys = [];
    for (const line of this.querySelectorAll('[data-line-key]')) {
      if (line instanceof HTMLElement && line.dataset.lineKey) {
        existingLineKeys.push(line.dataset.lineKey);
      }
    }

    const items = [];
    const sizeProperties = {};
    let totalQuantity = 0;

    for (const input of this.querySelectorAll('[data-variant-id][data-size]')) {
      if (!(input instanceof HTMLInputElement)) continue;

      const quantity = options.remove ? 0 : Math.max(0, Number(input.value) || 0);
      const size = input.dataset.size;
      if (!size) continue;

      sizeProperties[`_${size}`] = quantity.toString();
      totalQuantity += quantity;
    }

    const firstInput = this.querySelector('[data-variant-id][data-size]');
    if (firstInput instanceof HTMLInputElement && totalQuantity > 0) {
      items.push({
        id: Number(firstInput.dataset.variantId),
        quantity: totalQuantity,
        properties: sizeProperties,
      });
    }

    this.classList.add('cart-size-quantity-grid--updating');

    try {
      let sections;
      let itemCount;
      const sectionsPayload = Array.from(sectionIds).join(',');

      if (existingLineKeys.length > 0 && totalQuantity > 0) {
        const changeResponse = await fetch(
          Theme.routes.cart_change_url,
          fetchConfig('json', {
            body: JSON.stringify({
              id: existingLineKeys[0],
              quantity: totalQuantity,
              properties: sizeProperties,
              sections: sectionsPayload,
              sections_url: window.location.pathname,
            }),
          })
        );
        if (!changeResponse.ok) throw new Error(`Cart change failed: ${changeResponse.status}`);

        const changeCart = await changeResponse.json();
        if (changeCart.status) throw new Error(changeCart.description || changeCart.message || 'Cart change failed');

        sections = changeCart.sections;
        itemCount = changeCart.item_count;

        if (existingLineKeys.length > 1) {
          const duplicateUpdates = {};
          for (const duplicateKey of existingLineKeys.slice(1)) {
            duplicateUpdates[duplicateKey] = 0;
          }

          const removeDuplicateResponse = await fetch(
            Theme.routes.cart_update_url,
            fetchConfig('json', {
              body: JSON.stringify({
                updates: duplicateUpdates,
                sections: sectionsPayload,
                sections_url: window.location.pathname,
              }),
            })
          );
          if (!removeDuplicateResponse.ok) throw new Error(`Cart update failed: ${removeDuplicateResponse.status}`);

          const removeDuplicateCart = await removeDuplicateResponse.json();
          if (removeDuplicateCart.status) {
            throw new Error(removeDuplicateCart.description || removeDuplicateCart.message || 'Cart update failed');
          }

          sections = removeDuplicateCart.sections;
          itemCount = removeDuplicateCart.item_count;
        }
      } else if (existingLineKeys.length > 0 && totalQuantity === 0) {
        const removeUpdates = {};
        for (const key of existingLineKeys) {
          removeUpdates[key] = 0;
        }

        const removeResponse = await fetch(
          Theme.routes.cart_update_url,
          fetchConfig('json', {
            body: JSON.stringify({
              updates: removeUpdates,
              sections: sectionsPayload,
              sections_url: window.location.pathname,
            }),
          })
        );
        if (!removeResponse.ok) throw new Error(`Cart update failed: ${removeResponse.status}`);

        const removeCart = await removeResponse.json();
        if (removeCart.status) throw new Error(removeCart.description || removeCart.message || 'Cart update failed');

        sections = removeCart.sections;
        itemCount = removeCart.item_count;
      } else if (items.length > 0) {
        const addResponse = await fetch(
          Theme.routes.cart_add_url,
          fetchConfig('json', {
            body: JSON.stringify({
              items,
              sections: sectionsPayload,
              sections_url: window.location.pathname,
            }),
          })
        );
        if (!addResponse.ok) throw new Error(`Cart add failed: ${addResponse.status}`);

        const addCart = await addResponse.json();
        if (addCart.status) throw new Error(addCart.description || addCart.message || 'Cart add failed');

        sections = addCart.sections;
      }

      if (sections) {
        document.dispatchEvent(
          new CartUpdateEvent(undefined, 'cart-size-quantity-grid', {
            source: 'cart-size-quantity-grid',
            itemCount,
            sections,
          })
        );
      }
    } catch (error) {
      console.error(error);
      this.closest('.cart-items__table-row')?.classList.remove('removing');
    } finally {
      this.classList.remove('cart-size-quantity-grid--updating');
    }
  }

  #getCartSectionIds() {
    const sectionIds = new Set();
    for (const cartItemsComponent of document.querySelectorAll('cart-items-component')) {
      if (cartItemsComponent instanceof HTMLElement && cartItemsComponent.dataset.sectionId) {
        sectionIds.add(cartItemsComponent.dataset.sectionId);
      }
    }
    return sectionIds;
  }
}

if (!customElements.get('cart-size-quantity-grid')) {
  customElements.define('cart-size-quantity-grid', CartSizeQuantityGrid);
}
